/** Kanban board helpers — soft overlay on notes (settings, not hierarchy). */

export const KANBAN_MIN_COLS = 4;
export const KANBAN_MAX_COLS = 9;

export const DEFAULT_KANBAN_COLUMNS = [
  { id: "col_inbox", label: "Bandeja" },
  { id: "col_doing", label: "En curso" },
  { id: "col_wait", label: "Espera" },
  { id: "col_done", label: "Hecho" },
];

function newColId() {
  return `col_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

/** Clamp/normalize persisted board; fill missing notes into first column. */
export function normalizeKanbanBoard(raw, textIds) {
  const ids = Array.isArray(textIds) ? textIds.map(String) : [];
  const idSet = new Set(ids);

  let columns = Array.isArray(raw?.columns)
    ? raw.columns
        .filter((c) => c && typeof c.id === "string")
        .map((c) => ({
          id: String(c.id),
          label: String(c.label || "Columna").slice(0, 40),
        }))
    : [];

  if (columns.length < KANBAN_MIN_COLS) {
    const used = new Set(columns.map((c) => c.id));
    for (const def of DEFAULT_KANBAN_COLUMNS) {
      if (columns.length >= KANBAN_MIN_COLS) break;
      if (!used.has(def.id)) {
        columns.push({ ...def });
        used.add(def.id);
      }
    }
    while (columns.length < KANBAN_MIN_COLS) {
      const id = newColId();
      columns.push({ id, label: `Columna ${columns.length + 1}` });
    }
  }
  if (columns.length > KANBAN_MAX_COLS) columns = columns.slice(0, KANBAN_MAX_COLS);

  const colIds = columns.map((c) => c.id);
  const lists = {};
  for (const id of colIds) lists[id] = [];

  const seen = new Set();
  const rawLists = raw?.lists && typeof raw.lists === "object" ? raw.lists : {};
  for (const colId of colIds) {
    const arr = Array.isArray(rawLists[colId]) ? rawLists[colId] : [];
    for (const tid of arr) {
      const s = String(tid);
      if (!idSet.has(s) || seen.has(s)) continue;
      lists[colId].push(s);
      seen.add(s);
    }
  }

  // Unassigned → first column (stable: follow textIds order)
  const first = colIds[0];
  for (const tid of ids) {
    if (!seen.has(tid)) {
      lists[first].push(tid);
      seen.add(tid);
    }
  }

  return { columns, lists };
}

export function renameKanbanColumn(board, columnId, label) {
  const next = String(label || "").trim().slice(0, 40) || "Columna";
  return {
    ...board,
    columns: board.columns.map((c) => (c.id === columnId ? { ...c, label: next } : c)),
  };
}

export function addKanbanColumn(board, label = "Nueva") {
  if (board.columns.length >= KANBAN_MAX_COLS) return board;
  const id = newColId();
  return {
    columns: [...board.columns, { id, label: String(label).slice(0, 40) }],
    lists: { ...board.lists, [id]: [] },
  };
}

export function removeKanbanColumn(board, columnId) {
  if (board.columns.length <= KANBAN_MIN_COLS) return board;
  if (!board.columns.some((c) => c.id === columnId)) return board;
  const orphaned = board.lists[columnId] || [];
  const columns = board.columns.filter((c) => c.id !== columnId);
  const lists = { ...board.lists };
  delete lists[columnId];
  const first = columns[0].id;
  const merged = [...(lists[first] || [])];
  for (const tid of orphaned) {
    if (!merged.includes(tid)) merged.push(tid);
  }
  lists[first] = merged;
  return { columns, lists };
}

/** After rbd drag-end: move textId from source → dest index. */
export function applyKanbanDrag(board, { sourceCol, destCol, sourceIndex, destIndex, textId }) {
  if (!sourceCol || !destCol || !textId) return board;
  const lists = { ...board.lists };
  const src = [...(lists[sourceCol] || [])];
  const [moved] = src.splice(sourceIndex, 1);
  if (moved !== textId && moved != null) {
    // index/id mismatch — still move whatever was at index if present
  }
  const item = moved || textId;
  lists[sourceCol] = src;

  if (sourceCol === destCol) {
    const next = [...src];
    next.splice(destIndex, 0, item);
    lists[sourceCol] = next;
  } else {
    const dest = [...(lists[destCol] || [])].filter((id) => id !== item);
    dest.splice(destIndex, 0, item);
    lists[destCol] = dest;
  }
  return { ...board, lists };
}
